/**
 * React Query keys for polls (TECH-197). A poll is a kind of post, so the poll itself lives in the
 * question caches (`@lib/cache/questions`) and is written there by `writePollIntoCaches`; this module
 * holds only what is poll-specific.
 */
export const pollsBaseKey = () => ['polls'] as const;

/** Every voters list of one poll, for invalidating them all. */
export const pollVotersBaseKey = (questionId: string) =>
  [...pollsBaseKey(), 'voters', questionId] as const;

/** The "See who" drawer's list (B6): one option's voters, or every voter once without one. */
export const pollVotersKey = (questionId: string, optionId?: string | null) =>
  [...pollVotersBaseKey(questionId), optionId ?? 'all'] as const;

/**
 * One page list of `GET /polls/places` (the composer's picker, A4/A5): per type and search term,
 * the term as the picker sends it (trimmed), so "run" and "run " share one entry.
 */
export const pollPlacesKey = (type: 'spaces' | 'events', q: string) =>
  [...pollsBaseKey(), 'places', type, q.trim()] as const;

/** Every live poll read (TECH-867). */
export const liveBaseKey = () => [...pollsBaseKey(), 'live'] as const;

/**
 * The big screen's read of `GET /live/{code}/screen?key=` (polled every 2 s). The key segment is the
 * presenter key, a secret: the reader declares it to `queryCacheOnError` (`meta.searchKeySegment`,
 * `LIVE_SCREEN_KEY_SEGMENT`) so a failed read never logs it.
 */
export const liveScreenKey = (code: string, key: string) =>
  [...liveScreenBaseKey(code), key] as const;

/** Every big-screen read of one live poll (whatever key it was opened with), for writing a move into them. */
export const liveScreenBaseKey = (code: string) => [...liveBaseKey(), 'screen', code] as const;

/** Index of the presenter key inside `liveScreenKey`, for redacting it from logs. */
export const LIVE_SCREEN_KEY_SEGMENT = 4;

/**
 * Index of the live code inside `liveStateKey`, `liveMeKey` and `liveOpenKey`: the code is the room's link, so the
 * room's reads declare it to `queryCacheOnError` (`meta.searchKeySegment`) and a failure never logs it.
 */
export const LIVE_CODE_KEY_SEGMENT = 3;

/** The public `GET /live/{code}/state` (the phones' read, FE-L3a). */
export const liveStateKey = (code: string) => [...liveBaseKey(), 'state', code] as const;

/** The author's `GET /live/{code}/host` (the host view, FE-L1b): holds a plain `LiveHostState`. */
export const liveHostKey = (code: string) => [...liveBaseKey(), 'host', code] as const;

/** Your live polls, `GET /live` (FE-L1b's list): invalidated after a live poll is saved or edited (FE-L1a). */
export const livePollsListKey = () => [...liveBaseKey(), 'list'] as const;

/** The viewer's `GET /live/{code}/me` (FE-L3a): written at once by their own answers. */
export const liveMeKey = (code: string) => [...liveBaseKey(), 'me', code] as const;

/** `POST /live/{code}/open`, once per room per page (FE-L3a): its one-time guest claim, kept in memory. */
export const liveOpenKey = (code: string) => [...liveBaseKey(), 'open', code] as const;

/** A member's feed card read, `GET /questions/{id}/live` (FE-L3a, polled while on screen). */
export const questionLiveKey = (questionId: string) =>
  [...liveBaseKey(), 'question', questionId] as const;
